/**
 * Board runtime substrate — the shared state a board and its chrome both read,
 * split into TWO contexts on purpose:
 *
 *  - `BoardCommandsContext` (STABLE): the render root, the imperative command
 *    surface, viewport reporting/registration, and the operator flags the board
 *    itself consumes. Its value object identity changes ONLY when root / operator
 *    flags change — never on a pan/zoom/focal frame. The bare board
 *    (`BoardSurface`) and the forwardRef handle subscribe here, so a breadcrumb
 *    change never re-renders the board.
 *  - `BoardFocalContext` (VOLATILE): the current viewport, the derived breadcrumb
 *    `steps`, and the smart-view toggle. ONLY chrome subscribes here; it re-renders
 *    per focal change, the board does not.
 *
 * This is the "compose a bare board + the chrome you want inside one substrate"
 * seam: the substrate owns the coupling; the pieces are pure consumers.
 */
import { createContext, useContext } from "react";
import type { FocalStep, SpaceLike, Viewport } from "@archlang/scene/board";
import type { BoardCanvasHandle } from "./BoardCanvas";
import type { ExportFocalOptions } from "./exportPng";

/** The board's imperative command surface — stable closures over the canvas ref,
 *  callable by chrome buttons and the host handle alike. */
export interface BoardCommandSet {
  flyTo(moduleId: string): void;
  enterSpace(moduleId: string): void;
  reframe(): void;
  framePair(aId: string, bId: string): void;
  restoreViewport(vp: Viewport): void;
  setViewportImmediate(vp: Viewport): void;
  popOut(): void;
  resetView(): void;
  navigateTo(path: readonly string[]): void;
  exportFocalAsPng(opts?: ExportFocalOptions): Promise<void>;
  exportFocalAsSvg(opts?: ExportFocalOptions): Promise<void>;
  copyFocalToClipboard(opts?: ExportFocalOptions): Promise<void>;
}

/** STABLE runtime — identity fixed except when root / operator flags change. */
export interface BoardCommands {
  /** Shared render + focal root. */
  readonly root: SpaceLike;
  /** Board → substrate viewport report (applies the focalPath-identity bail). */
  readonly reportViewport: (vp: Viewport) => void;
  /** Callback ref: the mounted `BoardCanvas` registers its handle here. */
  readonly registerCanvas: (h: BoardCanvasHandle | null) => void;
  /** Current viewport from the canvas handle, falling back to the last reported
   *  one — a ref read, never the volatile state (so the handle bridge that calls
   *  it need not subscribe to focal). */
  readonly getViewport: () => Viewport;
  readonly commands: BoardCommandSet;
  /** → BoardCanvas `operatorEnabled` (smart-view motion active). */
  readonly operatorEnabled: boolean;
  /** → BoardCanvas `operatorInteractive` (user may drive the camera). */
  readonly operatorInteractive: boolean;
}

/** VOLATILE runtime — changes on focal / smart-view; only chrome subscribes. */
export interface BoardFocal {
  readonly viewport: Viewport | null;
  /** Breadcrumb path — `walkFocal(root, focalPath)`, or the root fallback when no
   *  viewport has been reported yet. */
  readonly steps: readonly FocalStep[];
  /** The smart-view pill: shown only when `showToggle`. */
  readonly operator: { readonly showToggle: boolean; readonly on: boolean; readonly toggle: () => void };
}

export const BoardCommandsContext = createContext<BoardCommands | null>(null);
export const BoardFocalContext = createContext<BoardFocal | null>(null);

/** The board's stable command runtime. Throws outside a `<BoardProvider>`. */
export function useBoardCommands(): BoardCommands {
  const v = useContext(BoardCommandsContext);
  if (!v) throw new Error("useBoardCommands must be used within a <BoardProvider>");
  return v;
}

/** The board's volatile focal runtime (chrome only). Throws outside a provider. */
export function useBoardFocal(): BoardFocal {
  const v = useContext(BoardFocalContext);
  if (!v) throw new Error("useBoardFocal must be used within a <BoardProvider>");
  return v;
}
